/**
 * <xp-recently-viewed> — remembers product handles in localStorage and renders
 * up to 4 previously viewed products (excluding the current one).
 *
 * Required markup:
 *   [data-handle]          — current product handle (attribute on the element)
 *   [data-recent-list]     — <ul> to fill
 *   template[data-recent-item] — item template with [data-recent-link], [data-recent-img],
 *                            [data-recent-title], [data-recent-price]
 * The element stays hidden until at least one other product is available.
 */
const STORAGE_KEY = 'xp-recently-viewed';
const MAX_STORED = 9;
const MAX_SHOWN = 4;

class XpRecentlyViewed extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const handle = this.dataset.handle;
    const stored = this.#read();
    const others = stored.filter((h) => h !== handle);
    if (handle) this.#write([handle, ...others].slice(0, MAX_STORED));
    if (others.length) this.#render(others.slice(0, MAX_SHOWN + 2));
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #read() {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter((h) => typeof h === 'string') : [];
    } catch {
      return [];
    }
  }

  #write(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
      /* storage unavailable: feature silently off */
    }
  }

  async #render(handles) {
    const root = window.Shopify?.routes?.root || '/';
    const results = await Promise.all(
      handles.map((h) =>
        fetch(`${root}products/${encodeURIComponent(h)}.js`, { signal: this.#ac.signal })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );
    const products = results.filter((p) => p && p.available !== false).slice(0, MAX_SHOWN);
    if (!products.length) return;

    const list = this.querySelector('[data-recent-list]');
    const template = this.querySelector('template[data-recent-item]');
    if (!list || !template) return;

    const currency = this.dataset.currency || window.Shopify?.currency?.active || 'CAD';
    const locale = this.dataset.locale || document.documentElement.lang || 'en';
    const money = new Intl.NumberFormat(locale, { style: 'currency', currency });

    const fragment = document.createDocumentFragment();
    for (const product of products) {
      const node = template.content.cloneNode(true);
      const link = node.querySelector('[data-recent-link]');
      const img = node.querySelector('[data-recent-img]');
      const title = node.querySelector('[data-recent-title]');
      const price = node.querySelector('[data-recent-price]');
      link.href = product.url;
      title.textContent = product.title;
      price.textContent = money.format((product.price || 0) / 100);
      if (product.featured_image) {
        const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
        const url = new URL(src);
        url.searchParams.set('width', '600');
        img.src = url.toString();
        img.alt = product.title;
      } else {
        img.remove();
      }
      fragment.append(node);
    }
    list.replaceChildren(fragment);
    this.hidden = false;
  }
}

if (!customElements.get('xp-recently-viewed')) {
  customElements.define('xp-recently-viewed', XpRecentlyViewed);
}
